import React, { useState, useContext, useEffect, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { format, parseISO } from "date-fns";
import { MenuContext } from "src/context/MenuContext";
import { useAuth } from "src/hooks/useAuth";
import { getProjects } from "src/store/project";
import { getMcode } from "src/store/mcode";
import {
  updateDailyWorks,
  getSingleDailyWorks,
  deleteSingleData,
  deleteData,
  addDailyWorks,
  getDailyWorksByDate
} from "src/store/schedule";
import ScheduleEditValidationSchema from "src/validation/ScheduleEditValidationSchema";
import Menu from "src/components/common/Menu";
import Header from "src/components/common/Header";
import Popup from "src/components/schedule/SchedulePopup";
import CustomDatePicker from "src/components/common/CustomDatePicker";
import { ValidationMessages } from "src/components/common/ValidationMessage";
import ScrollTop from "src/components/common/ScrollTop";
import TaskList from "src/components/schedule/ScheduleInputTask";
import MemoSection from "src/components/schedule/ScheduleInputMemo";
import ProgressMeter from "src/components/schedule/ScheduleProgressMeter";
import { getUsers } from "src/store/user";
import CustomLoader from "src/components/common/CustomLoader";
import TimeSelector from "src/components/common/TimeSelector"

export default function ScheduleEdit() {
  const { id, work_date } = useParams();
  const { user: userData } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { visibleMenu } = useContext(MenuContext);
  const calendarRef = useRef(null);
  const [myMemo, setMyMemo] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [holiday_cd, setHolidayCd] = useState(null);
  const [validationMessages, setValidationMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [showTimeSelector, setShowTimeSelector] = useState(false);

  const projectList = useSelector((state) => state.project.data);
  const workingHoursCd = useSelector((state) => state.mcode.data.workinghourscd);
  const holidayData = useSelector((state) => state.mcode.data.holiday);
  const edit = useSelector((state) => state.schedule.singleData);
  const user = useSelector((state) => state.user.data);

   const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ScheduleEditValidationSchema),
    mode: "onSubmit",
    defaultValues: {
      work_date: date,
      work_start_time: "",
      work_end_time: getCurrentTime(),
      daily_memo: "",
      tasks: [],
      holidayArea: [],
    },
  });

  useEffect(() => {
    const currentTasks = watch("tasks") || [];
    currentTasks.forEach((task, index) => {
      timeprogress(index, task.progress_rate);
    });
  }, [watch("tasks"), register("work_start_time")]);

  const {
    fields: tasks,
    append: appendTask,
    remove: removeTask,
    replace: replaceTask,
  } = useFieldArray({
    control,
    name: "tasks",
  });

  useEffect(() => {
    // Add event listener for clicks
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      // Cleanup the event listener on unmount
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  

  const handleClickOutside = (event) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target)) {
      // If the click is outside the calendar, close it
      setIsCalendarOpen(false);
      setShowTimeSelector(false);
    }
  };

  const {
    fields: holidayArea,
    append: appendHoliday,
    remove: removeHoliday,
    replace: replaceHoliday,
  } = useFieldArray({
    control,
    name: "holidayArea",
  });
  const tasksData = watch("tasks") || [];
  const totalProgress = tasksData.reduce(
    (acc, task) => acc + (task.progress_rate || 0),
    0
  );

  useEffect(() => {
    setDate(work_date);
  }, [work_date]);

  useEffect(() => {
    setLoading(true);
    dispatch(getProjects());
    dispatch(getMcode({ section_cd: "workinghourscd,holiday" }));
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (userData && workingHoursCd) {
      const matchedWorkingHours = workingHoursCd.find(
        (item) => item.cd === userData.working_hours_cd
      );
      if (matchedWorkingHours) {
        setValue("work_start_time", matchedWorkingHours.code_value1);
      }
    }
  }, [userData, workingHoursCd, setValue]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    let loginId = userData ? JSON.parse(userData).id : null;
    const userMatch = user.find((val) => val.id === loginId);
    if (userMatch) {
        setMyMemo(userMatch.my_memo || '');
    } else {
        setMyMemo(''); 
    }
}, [user]);

  useEffect(() => {
    const extractErrors = (errorObj) => {
      let messages = new Set(); // Use Set() to store unique values

      for (const [key, value] of Object.entries(errorObj)) {
        if (value?.message) {
          messages.add(value.message); // Use .add() instead of .push()
        } else if (typeof value === "object") {
          for (const msg of extractErrors(value)) {
            messages.add(msg); // Ensure unique messages
          }
        }
      }

      return messages; // Returning a Set() preserves uniqueness
    };

    const messagesSet = extractErrors(errors);
    setValidationMessages([...messagesSet]); // Convert Set back to an array
  }, [errors]);

  useEffect(() => {
    let initialDate = work_date ? new Date(work_date) : new Date();
    if (isNaN(initialDate.getTime())) {
      initialDate = new Date();
    }
    const formattedDate = format(initialDate, "yyyy-MM-dd");
    setLoading(true);
    dispatch(getSingleDailyWorks({ id, work_date: formattedDate }))
      .then((response) => {
        if (response.payload) {
          setLoading(false);
        }
        if (response?.errors) {
          setValidationMessages(response.errors.message);
        }
        if (response.payload?.errors) {
          setValidationMessages(response.payload.errors);
        }
      })
      .catch((error) => {
        alert(error);
      });
  }, [dispatch, id, work_date]);

  useEffect(() => {
    if (edit) {
      const rawStartTime =  edit.work_start_datetime?.split(" ")[1]?.slice(0, 5) || "9:00";
      const rawEndTime   =  edit.work_end_datetime?.split(" ")[1]?.slice(0, 5) || getCurrentTime();
  
      setValue("work_start_time", rawStartTime);
      setValue("work_end_time", rawEndTime);

      let initialDate =
        edit.work_date && !isNaN(new Date(edit.work_date)) ? new Date(edit.work_date) : new Date();

      setValue("work_date", initialDate);
      setDate(format(initialDate, "yyyy-MM-dd"));
      setSelectedDate(initialDate);

      setValue("daily_memo", edit.daily_memo || "");

      const holiday_cd = edit?.holiday_cd || null;
      setHolidayCd(holiday_cd);

      const taskList = edit.task_list || [];
      const formattedTasks = taskList
        .filter((task) => task.t_project_id !== 0 && task.t_client_id !== 0)
        .map((task) => {
          // Reject tasks with missing project or client details
          if (
            !task.t_project_id ||
            !task.t_client_id ||
            !task.project_name_abbreviation ||
            !task.client_name_abbreviation
          ) {
            return null; // Mark invalid tasks as null
          }

          // Map valid tasks
          return {
            id_Api: task.id || null,
            project_id: task.t_project_id || 0,
            client_id: task.t_client_id || 0,
            client_name: task.client_name_abbreviation || null,
            project_name: task.project_name_abbreviation || null,
            holiday_cd: task.holiday_cd || holiday_cd || null,
            memo: task.task_detail || "",
            progress_rate: task.progress_rate || 0,
            work_minute: task.work_minute || 0,
            work_cost_price: task.work_cost_price || 0,
            order_no: task.order_no || null,
            t_daily_work_task_id: task.t_daily_work_task_id || null,
          };
        })
        .filter((task) => task !== null);

      replaceTask(formattedTasks);

      if (holiday_cd) {
        const matchingHoliday = Array.isArray(holidayData)
          ? holidayData.find((holiday) => holiday.cd === String(holiday_cd))
          : null;

        const taskDetail = taskList.find(
          (task) => task.t_project_id === 0 && task.t_client_id === 0
        );

        const holidayArea = {
          holiday_cd: holiday_cd || null,
          holiday_name: matchingHoliday ? matchingHoliday.code_value1 : "",
          memo: taskDetail ? taskDetail.task_detail : "",
          t_daily_work_task_id: taskDetail?.id || null,
        };

        replaceHoliday(holidayArea);
      } else {
        replaceHoliday([]);
      }

      setValidationMessages([]);
    }
  }, [edit, setValue, replaceTask, replaceHoliday, holidayData]);

  useEffect(() => {
    if (validationMessages.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validationMessages]);

  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  useEffect(() => {
    if (id && id !== "0") {
      // Fetch existing record for editing
      const formattedDate = format(new Date(work_date), "yyyy-MM-dd");
      setLoading(true);
      dispatch(getSingleDailyWorks({ id, work_date: formattedDate }))
        .then((response) => {
          if (response.payload) {
            setLoading(false);
          }
        })
        .catch((error) => {
          alert(error);
        });
    } else {
      // Clear form for creating a new record
      setValue("work_date", new Date());
      setValue("work_start_time", "9:00");
      setValue("work_end_time", "18:00");
      setValue("daily_memo", "");
      replaceTask([]);
      replaceHoliday([]);
    }
  }, [dispatch, id, work_date, setValue, replaceTask, replaceHoliday]);

  useEffect(() => {
    if (date) {
      const data = {
        t_user_id: userData?.id ?? null,
        work_date: new Date(date),
        copy_type: "specific",
        allowPreviousCopy: true,
      };
      fetchSpecificDayData(data);
    }
  }, [date]);

  const addTask = (selectedProject) => {
    const newTask = {
      project_id: selectedProject.id,
      client_id: selectedProject.client_id,
      client_name: selectedProject.client_name,
      project_name: selectedProject.name_abbreviation,
      holiday_cd: null,
      memo: "",
    };
    appendTask(newTask);
  };

  const addHoliday = (selectedHoliday) => {
    setHolidayCd(selectedHoliday.value);
    const newholidayArea = {
      holiday_cd: selectedHoliday.value,
      holiday_name: selectedHoliday.label,
      memo: "",
    };
    appendHoliday(newholidayArea);
  };

  const copyMemoToDaily = () => {
    setValue("daily_memo", myMemo);
  };

  const fetchSpecificDayData = (data) => {
    setLoading(true);
    dispatch(getDailyWorksByDate(data))
      .then((response) => {
        if (response.payload?.errors) {
          alert(response.payload.errors);
          return;
        }

        const fetchedData = response.payload?.data;
        if (fetchedData) {
          setValue("daily_memo", fetchedData.daily_memo || "");
          // Handle tasks
          const taskList = fetchedData.task_list || [];
          const formattedTasks = taskList
            .filter(task => task.t_client_id !== 0 || task.t_project_id !== 0)
            .map((task, index) => ({
              id_Api: task.id || null,
              project_id: task.t_project_id || 0,
              client_id: task.t_client_id || 0,
              client_name: task.client_name_abbreviation || "Unknown Client",
              project_name: task.project_name_abbreviation || "Unknown Project",
              memo: task.task_detail || "",
              progress_rate: task.progress_rate || 0,
              work_minute: task.work_minute || 0,
              work_cost_price: task.work_cost_price || 0,
              order_no: index + 1
            }));

          replaceTask(formattedTasks);

          // Handle holiday
          const holiday_cd = fetchedData.holiday_cd || null;
          setHolidayCd(holiday_cd);

          // Only create holiday area if holiday_cd exists and is not 0
          if (holiday_cd && holiday_cd !== 0) {
            const matchingHoliday = Array.isArray(holidayData) ? holidayData.find((holiday) => String(holiday.cd) === String(holiday_cd)) : null;
            const leaveTask = taskList.find((task) => task.t_client_id === 0 && task.t_project_id === 0);

            if (matchingHoliday) {
              const holidayArea = {
                holiday_cd: holiday_cd,
                holiday_name: matchingHoliday.code_value1,
                memo: leaveTask?.task_detail || "",
                t_daily_work_task_id: leaveTask?.id || null, // Set the id from leaveTask
              };
              replaceHoliday([holidayArea]);
            } else {
              replaceHoliday([]);
            }
          } else {
            replaceHoliday([]);
          }

          setValidationMessages([]);
        } else {
          setValue("daily_memo", "");
          replaceTask([]);
          replaceHoliday([]);
          setHolidayCd(null);
          alert("No previous data found.");
        }
      })
      .catch((error) => {
        console.error("Error fetching previous day data:", error);
        alert("An error occurred while fetching data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDateChange = (date) => {
    if (isValidDate(date)) {
      setIsCalendarOpen(false);
      const formattedDate = format(date, "yyyy-MM-dd");
      setValue("work_date", formattedDate);
      setDate(formattedDate);
      setSelectedDate(date); // Store the actual Date object instead of formatted string
    }
  };

  const handleSliderChange = (index, value) => {
    const currentTasks = watch("tasks") || [];
    const newTotal = currentTasks.reduce((sum, task, i) => {
      if (i === index) return sum + value;
      return sum + (task.progress_rate || 0);
    }, 0);

    if (newTotal <= 100) {
      setValue(`tasks.${index}.progress_rate`, value);
      timeprogress(index, value);
      return true;
    } else {
      return false;
    }
  };

  const isValidDate = (date) => {
    return date instanceof Date && !isNaN(date);
  };

  const timeprogress = (index, prog) => {
    const days = watch("work_date");
    const start = watch("work_start_time");
    const end = watch("work_end_time");
    if (days && start && end) {
      const formattedDate = new Date(days).toISOString().split("T")[0];

      const starttime = new Date(`${formattedDate}T${start}:00`).getTime();
      const endtime = new Date(`${formattedDate}T${end}:00`).getTime();

      let grosstotal = (endtime - starttime) / (60 * 60 * 1000);
      if (grosstotal < 0) grosstotal += 24;
      const recesstime = grosstotal >= 6 ? 1 : 0;
      const nettotal = grosstotal - recesstime;
      const times = (nettotal * 60 * prog) / 100 / 60;
      const H = Math.floor(times);
      const m = Math.floor((times - H) * 60);
      const timeDisplay = document.querySelector(`#progress_time_${index}`);
      if (timeDisplay) {
        timeDisplay.textContent = `${H}hours${m}minutes`;
      }
    }
  };

  const onSubmit = async (data) => {
    try {
      // Validate and format work_start_datetime
      const work_start_datetime = new Date(data.work_date);
      if (isNaN(work_start_datetime)) throw new Error("Invalid work_date");

      const [hours, minutes] = data.work_start_time?.split(":") || [];
      if (!hours || !minutes) throw new Error("Invalid work_start_time");

      work_start_datetime.setHours(hours, minutes);
      const formattedDate = work_start_datetime
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");

      // Prepare tasksData
      let tasksData = data.tasks
        .map((task) => ({
          cl_id: task.client_id ?? 0,
          pr_id: task.project_id ?? 0,
          memo: task.memo ?? null,
          progress_rate: task.progress_rate ?? 0,
          work_minute: task.work_minute ?? 0,
          work_cost_price: task.work_cost_price ?? 0,
          order_no: task.order_no ?? 1,
        }))
        .filter((task) => task.cl_id && task.pr_id); // Filter out invalid tasks

      // Add holiday data to tasksData
      if (data.holidayArea?.length > 0) {
        data.holidayArea.forEach((holiday) => {
          tasksData.push({
            cl_id: 0,
            pr_id: 0,
            memo: holiday.memo || null,
            progress_rate: 0,
            work_minute: null,
            work_cost_price: null,
            order_no: tasksData.length + 1,
          });
        });
      }

      // Prepare submitData
      const submitData = {
        id: id && id !== "0" ? id : null,
        t_user_id: userData?.id ?? null,
        work_date: format(work_start_datetime, "yyyy/MM/dd"),
        work_start_time: data.work_start_time || "9:00:00",
        work_end_time: data.work_end_time || "18:00:00",
        daily_memo: data.daily_memo || null,
        task_details: tasksData,
        holiday_cd: holiday_cd || null,
        work_start_datetime: formattedDate,
        my_memo: myMemo,
      };

      setLoading(true);

      // Split logic for insert and update
      if (submitData.id) {
        // Update existing record
        const response = await dispatch(updateDailyWorks(submitData)).unwrap();
        if (response) {
          navigate(`/scheduleList/${work_date}`);
        }
      } else {
        const response = await dispatch(addDailyWorks(submitData)).unwrap();
        if (response) {
          navigate(`/scheduleList/${work_date}`);
        }
      }
    } catch (error) {
      console.error("Error submitting data:", error);
      setValidationMessages([error.message || "An unexpected error occurred."]);
    } finally {
      setLoading(false);
    }
  };

  const deleteSingleTask = async (taskId, index) => {
    const userConfirmed = window.confirm(
      "Are you sure you want to delete this task?"
    );
    if (!userConfirmed) return;

    setLoading(true);

    try {
      const response = await dispatch(deleteSingleData(taskId));
      let payload = response.payload;

      // Parse JSON if payload is a string
      if (typeof payload === "string") {
        try {
          const jsonMatch = payload.match(/\{.*\}/);
          if (jsonMatch) {
            payload = JSON.parse(jsonMatch[0]);
          } else {
            throw new Error("Failed to extract JSON from payload.");
          }
        } catch (error) {
          console.error("Failed to parse payload:", error);
          setLoading(false);
          return;
        }
      }

      // Proceed if deleted successfully
      if (payload?.message === "Record deleted successfully") {
        alert(payload.message);

        const updatedData = await dispatch(
          getDailyWorksByDate({
            t_user_id: userData?.id ?? null,
            work_date: date,
            copy_type: "specific",
          })
        );

        const data = updatedData.payload?.data;

        if (data) {
          // ✅ Handle task list (without modifying the leave)
          const taskList = data.task_list || [];
          const formattedTasks = taskList
            .filter((task) => task.t_client_id !== 0 || task.t_project_id !== 0) // Exclude leave-type
            .map((task, index) => ({
              project_id: task.t_project_id || 0,
              client_id: task.t_client_id || 0,
              client_name: task.client_name_abbreviation || "Unknown Client",
              project_name: task.project_name_abbreviation || "Unknown Project",
              memo: task.task_detail || "",
              progress_rate: task.progress_rate || 0,
              work_minute: task.work_minute || 0,
              work_cost_price: task.work_cost_price || 0,
              order_no: task.order_no || index + 1,
              id_Api: task?.id,
            }));

          replaceTask(formattedTasks);

          // ✅ Handle holiday (leave) separately
          const holiday_cd = data.holiday_cd;

          if (holiday_cd) {
            const matchingHoliday = Array.isArray(holidayData)
              ? holidayData.find((holiday) => holiday.cd === String(holiday_cd))
              : null;

            const taskDetail = taskList.find(
              (task) => task.t_project_id === 0 && task.t_client_id === 0
            );

            const holidayArea = {
              holiday_cd: holiday_cd || null,
              holiday_name: matchingHoliday ? matchingHoliday.code_value1 : "",
              memo: taskDetail ? taskDetail.task_detail : "",
              t_daily_work_task_id: taskDetail?.id || null,
            };
            replaceHoliday(holidayArea);
          } else {
            replaceHoliday([]);
          }

        }
      }
      setLoading(false);
    } catch (error) {
      setValidationMessages([
        error.message || "An error occurred while deleting the task.",
      ]);
      setLoading(false);
    }
  };

  const deleteBtn = () => {
    const userConfirmed = window.confirm(
      "Are you sure you want to delete All data?"
    );
    if (!userConfirmed) {
      return; // Exit if the user cancels the confirmation
    }
    setLoading(true);
    dispatch(deleteData(id))
      .then((response) => {
        if (response.payload.status) {
          setLoading(false);
        }
        navigate(`/scheduleList/${work_date}`);
      })
      .catch((error) => {
        alert(error);
      });
  };

  return (
    <>
      <div className="schedule-edit">
        <div className="wrapper">
          {visibleMenu && <Menu />}
          <div className={visibleMenu ? "menu-open" : "menu-closed"}>
            <div className="navbar navbar-fixed-top navbar-absolute">
              <Header sx={{ mb: 4 }} />
            </div>
            {loading ? (
              <CustomLoader />
            ) : (
              <section className="container-fluid contents-top">
                <form onSubmit={handleSubmit(onSubmit)}>
                  {validationMessages && validationMessages.length > 0 && (
                    <div style={{ background: "#ffe4e2" }}>
                      <ValidationMessages
                        messages={validationMessages}
                        onClose={() => setValidationMessages([])}
                      />
                    </div>
                  )}
                  <div className="form-inline" id="search_form">
                    <div className="row calender-box">
                      <div className="col-xs-1"></div>
                      <div className="col-xs-10 text-center">
                        <h1 className="input-group datepicker-hl jq-datetimepicker">
                          <input
                            type="text"
                            name="work_date"
                            value={format(parseISO(date), "yyyy/MM/dd EEEE")}
                            readOnly
                            onClick={() => {
                              setIsCalendarOpen(!isCalendarOpen);
                            }}
                            {...register("work_date")}
                          />
                          <span
                            className="input-group-addon"
                            onClick={() => {
                              setIsCalendarOpen(!isCalendarOpen);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <span className="glyphicon glyphicon-calendar"></span>
                          </span>
                          {isCalendarOpen && (
                            <div
                              ref={calendarRef}
                              style={{
                                position: "absolute",
                                zIndex: 1000,
                                marginLeft: 52,
                              }}
                            >
                              <CustomDatePicker
                                workDate={selectedDate}
                                handleDateChange={handleDateChange}
                                isValidDate={isValidDate}
                              />
                            </div>
                          )}
                        </h1>
                        {errors.work_date && (
                          <p className="text-danger">
                            {errors.work_date.message}
                          </p>
                        )}
                      </div>
                      <div className="col-xs-1 text-right"></div>
                    </div>
                    <div className="row">
                      <div className="col-xs-12 col-sm-6 text-left">
                        <div
                          className="form-group"
                          style={{ display: "inline-block" }}
                        >
                          <label className="sr-only" htmlFor="InputTime1">
                            出勤時刻
                          </label>
                          <p
                            className="form-control"
                            readOnly
                            style={{ margin: 0 }}
                            {...register("work_start_time")}
                          >
                            {watch("work_start_time")}
                          </p>
                          <div style={{ display: "inline-block" }}>
                            &nbsp;～&nbsp;
                          </div>
                          <p className="form-control" ref={calendarRef} onClick={() => setShowTimeSelector(!showTimeSelector)} style={{ margin: 0, cursor: 'pointer' }}>{watch("work_end_time")}</p>
                          {showTimeSelector && (
                            <div ref={calendarRef} >
                              <TimeSelector
                                value={watch("work_end_time")}
                                onChange={(e) => {
                                  setValue("work_end_time", e.target.value);
                                  
                                }}
                              />
                            </div>
                          )}
                          {errors.work_start_time && (
                            <p className="text-danger">
                              {errors.work_start_time.message}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="col-xs-12 col-sm-6 input_menu text-right">
                        <button
                          type="button"
                          className="btn btn-default jq-popup-open"
                          style={{ marginRight: "5px" }}
                          onClick={togglePopup}
                        >
                          <span
                            className="glyphicon glyphicon-plus"
                            aria-hidden="true"
                          ></span>
                        </button>
                        <div
                          style={{
                            display: "inline-block",
                            position: "relative",
                            marginRight: "5px",
                          }}
                        ></div>
                        <button
                          id="trash_btn"
                          type="button"
                          className="btn btn-default"
                          onClick={deleteBtn}
                        >
                          <span
                            className="glyphicon glyphicon-trash"
                            aria-hidden="true"
                          ></span>
                        </button>
                      </div>
                    </div>
                    <textarea
                      style={{ display: "none" }}
                      name="copy_my_memo"
                      id="copy_my_memo"
                      value={myMemo || ""}
                      readOnly
                    ></textarea>
                  </div>
                  <section className="container">
                    <div className="work_form" id="work_form">
                      <div className="row">
                        <div className="holiday_area">
                          <input
                            type="hidden"
                            value="33018"
                            name="daily_work_id"
                          />
                        </div>
                        <TaskList tasks={tasks} holidayArea={holidayArea} removeTask={removeTask} removeHoliday={removeHoliday} register={register} control={control} errors={errors} handleSliderChange={handleSliderChange} isEditMode={true} deleteSingleTask={deleteSingleTask} />
                      </div>
                      <ProgressMeter totalProgress={totalProgress} />
                      <MemoSection register={register} myMemo={myMemo} setMyMemo={setMyMemo} copyMemoToDaily={copyMemoToDaily} isEditMode={true} />
                      <div className="col-xs-12 form-button">
                        <button type="submit" className="btn btn-success btn-lg btn-block jq_regist_btn">Save</button>
                      </div>
                    </div>
                  </section>
                </form>
                {isPopupOpen && (<Popup isOpen={isPopupOpen} togglePopup={togglePopup} addTask={addTask} addHoliday={addHoliday} projectList={projectList} existProject={tasks} id={id} holidayArea={holidayArea} />)}
              </section>
            )}
          </div>
        </div>
      </div>
      <ScrollTop />
    </>
  );
}
